import { useTracker } from '@volvo-cars/tracking';
/**
 * Represents a button component used for expanding and collapsing content.
 */
import type React from 'react';

/**
 * Props for the ExpandedButton component.
 */
export type Props = {
  /**
   * Determines whether the expanded content is shown or not.
   */
  showMoreExpanded: boolean;
  /**
   * Function to toggle the state of `showMoreExpanded`.
   */
  setShowMoreExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * The ID of the toggle button.
   */
  toggleId: string;
  /**
   * Optional text to display for the "show more" button.
   */
  showMoreText?: string;
  /**
   * Optional text to display for the "show less" button.
   */
  showLessText?: string;
  /**
   * Data source for the "Show more" button.
   */
  showMoreSource?: string;
  /**
   * Data source for the "Show less" button.
   */
  showLessSource?: string;
};

const ExpandedButton: React.FC<Props> = ({
  showMoreExpanded,
  setShowMoreExpanded,
  toggleId,
  showMoreText,
  showLessText,
  showMoreSource,
  showLessSource,
}) => {
  const tracker = useTracker(
    null,
    { forceLowerCase: true },
    {
      ignoreIfEmptyContext: true,
    },
  );

  return (
    <div className="button-group justify-center mt-24">
      <button
        type="button"
        className="button-text"
        aria-expanded={showMoreExpanded}
        aria-controls={toggleId}
        data-arrow={showMoreExpanded ? 'up' : 'down'}
        onClick={(event) => {
          tracker.interaction({
            eventAction: showMoreExpanded
              ? 'accordion|collapse'
              : 'accordion|expand',
            eventLabel: showMoreExpanded ? 'show less' : 'show more',
          });
          setShowMoreExpanded((expanded) => !expanded);
          if (
            !showMoreExpanded &&
            event.currentTarget.matches(':focus-visible')
          ) {
            const firstSummary = document
              .getElementById(
                event.currentTarget.getAttribute('aria-controls') as string,
              )
              ?.querySelector(':scope details summary') as HTMLElement | null;
            setTimeout(() => {
              firstSummary?.focus();
            }, 10);
          }
        }}
        data-sources={[showLessSource, showMoreSource]}
      >
        {showMoreExpanded ? showLessText : showMoreText}
      </button>
    </div>
  );
};

export default ExpandedButton;
